<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  FREQUENCY_OPTIONS,
  buildRecurrenceDescription,
} from 'dashboard/helper/recurrenceHelpers';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object,
    default: null,
  },
  scheduledDate: {
    type: Date,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'close']);

const { t, locale } = useI18n();

const frequency = ref('weekly');
const interval = ref(1);
const weekDays = ref([]);
const monthlyType = ref('day_of_month');
const monthlyWeek = ref(1);
const monthlyWeekday = ref(0);
const endType = ref('never');
const endDate = ref('');
const endCount = ref(10);

const WEEKDAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const END_OPTIONS = [
  {
    value: 'never',
    labelKey: 'ENDS_NEVER',
    icon: 'i-lucide-infinity',
  },
  {
    value: 'on_date',
    labelKey: 'ENDS_ON_DATE',
    icon: 'i-lucide-calendar-days',
  },
  {
    value: 'after_count',
    labelKey: 'ENDS_AFTER',
    icon: 'i-lucide-list-checks',
  },
];

const isWeekly = computed(() => frequency.value === 'weekly');
const isMonthly = computed(() => frequency.value === 'monthly');

const draftRule = computed(() => {
  const rule = {
    frequency: frequency.value,
    interval: interval.value || 1,
    end_type: endType.value,
  };

  if (isWeekly.value) {
    rule.week_days = [...weekDays.value].sort();
  }

  if (isMonthly.value) {
    rule.monthly_type = monthlyType.value;
    if (monthlyType.value === 'day_of_week') {
      rule.monthly_week = monthlyWeek.value;
      rule.monthly_weekday = monthlyWeekday.value;
    } else if (props.scheduledDate) {
      rule.month_day = props.scheduledDate.getDate();
    }
  }

  if (frequency.value === 'yearly' && props.scheduledDate) {
    rule.year_day = props.scheduledDate.getDate();
    rule.year_month = props.scheduledDate.getMonth() + 1;
  }

  if (endType.value === 'on_date' && endDate.value) {
    rule.end_date = endDate.value;
  }

  if (endType.value === 'after_count') {
    rule.end_count = endCount.value;
  }

  return rule;
});

const previewText = computed(() =>
  buildRecurrenceDescription(draftRule.value, t, locale.value)
);

const hasValidWeekDays = computed(
  () => !isWeekly.value || weekDays.value.length > 0
);

const hasValidEndDate = computed(
  () => endType.value !== 'on_date' || endDate.value
);

const hasValidEndCount = computed(
  () => endType.value !== 'after_count' || endCount.value >= 1
);

const hasValidInterval = computed(() => interval.value >= 1);

const isValid = computed(
  () =>
    hasValidInterval.value &&
    hasValidWeekDays.value &&
    hasValidEndDate.value &&
    hasValidEndCount.value
);

const weekdayLabel = index =>
  t(`SCHEDULED_MESSAGES.RECURRENCE.WEEKDAYS_MED.${WEEKDAY_KEYS[index]}`);

const weekdayFullLabel = index =>
  t(`SCHEDULED_MESSAGES.RECURRENCE.WEEKDAYS_FULL.${WEEKDAY_KEYS[index]}`);

const frequencyLabel = freq =>
  t(
    `SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.FREQ_LABEL_${freq.toUpperCase()}`
  );

const toggleWeekDay = day => {
  const idx = weekDays.value.indexOf(day);
  if (idx === -1) {
    weekDays.value.push(day);
  } else if (weekDays.value.length > 1) {
    weekDays.value.splice(idx, 1);
  }
};

const resetToDefaults = () => {
  frequency.value = 'weekly';
  interval.value = 1;
  weekDays.value = props.scheduledDate ? [props.scheduledDate.getDay()] : [1];
  monthlyType.value = 'day_of_month';
  monthlyWeek.value = 1;
  monthlyWeekday.value = 0;
  endType.value = 'never';
  endDate.value = '';
  endCount.value = 10;
};

const initFromRule = rule => {
  if (!rule) {
    resetToDefaults();
    return;
  }
  frequency.value = rule.frequency || 'weekly';
  interval.value = rule.interval || 1;
  weekDays.value = rule.week_days ? [...rule.week_days] : [];
  monthlyType.value = rule.monthly_type || 'day_of_month';
  monthlyWeek.value = rule.monthly_week || 1;
  monthlyWeekday.value = rule.monthly_weekday || 0;
  endType.value = rule.end_type || 'never';
  endDate.value = rule.end_date || '';
  endCount.value = rule.end_count || 10;
};

const onDone = () => {
  if (!isValid.value) return;
  emit('update:modelValue', draftRule.value);
  emit('close');
};

const onCancel = () => {
  emit('close');
};

watch(
  () => props.show,
  isVisible => {
    if (isVisible) {
      initFromRule(props.modelValue);
      if (!weekDays.value.length && props.scheduledDate) {
        weekDays.value = [props.scheduledDate.getDay()];
      }
    }
  }
);

watch(frequency, newFrequency => {
  if (newFrequency === 'weekly' && weekDays.value.length === 0) {
    weekDays.value = props.scheduledDate ? [props.scheduledDate.getDay()] : [1];
  }
});
</script>

<template>
  <woot-modal
    :show="show"
    size="small"
    class="[&_.modal-container]:!w-[32rem] [&_.modal-container]:!max-w-[90%]"
    @close="onCancel"
  >
    <div class="flex w-full flex-col">
      <div class="px-6 pb-5 pt-6">
        <h3 class="text-lg font-semibold tracking-tight text-n-slate-12">
          {{ t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.TITLE') }}
        </h3>
        <p v-if="previewText" class="mt-1.5 text-sm leading-5 text-n-slate-11">
          {{ previewText }}
        </p>
      </div>

      <div class="flex flex-col gap-6 px-6 pb-1">
        <section class="flex flex-col gap-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.REPEAT_EVERY') }}
          </h4>

          <div class="flex items-center gap-2">
            <input
              v-model.number="interval"
              type="number"
              min="1"
              class="!h-10 !w-16 shrink-0 rounded-lg bg-n-alpha-black2 px-2 text-center text-sm font-medium tabular-nums text-n-slate-12 outline-none ring-1 ring-n-weak transition-[box-shadow] focus:ring-n-brand"
            />

            <select
              v-model="frequency"
              class="h-10 min-w-0 flex-1 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm font-medium text-n-slate-12 outline-none ring-1 ring-n-weak transition-[box-shadow] focus:ring-n-brand"
            >
              <option
                v-for="freq in FREQUENCY_OPTIONS"
                :key="freq"
                :value="freq"
              >
                {{ frequencyLabel(freq) }}
              </option>
            </select>
          </div>

          <div v-if="isWeekly" class="flex flex-col gap-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.REPEAT_ON') }}
            </span>
            <div class="grid grid-cols-7 gap-1">
              <button
                v-for="(key, index) in WEEKDAY_KEYS"
                :key="key"
                type="button"
                class="flex h-9 min-w-0 items-center justify-center rounded-lg text-[11px] font-medium transition-colors active:scale-[0.97]"
                :class="
                  weekDays.includes(index)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
                :title="weekdayFullLabel(index)"
                :aria-label="weekdayFullLabel(index)"
                :aria-pressed="weekDays.includes(index)"
                @click="toggleWeekDay(index)"
              >
                {{ weekdayLabel(index) }}
              </button>
            </div>
          </div>

          <div v-if="isMonthly" class="flex flex-col gap-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.MONTHLY_TYPE') }}
            </span>
            <div class="grid grid-cols-2 gap-2">
              <label
                class="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm transition-colors"
                :class="
                  monthlyType === 'day_of_month'
                    ? 'bg-n-brand/10 text-n-slate-12 ring-1 ring-n-brand'
                    : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
                "
              >
                <input
                  v-model="monthlyType"
                  type="radio"
                  value="day_of_month"
                  class="!size-4 !w-4 shrink-0 accent-n-brand"
                />
                {{
                  t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.MONTHLY_ON_DAY')
                }}
              </label>
              <label
                class="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm transition-colors"
                :class="
                  monthlyType === 'day_of_week'
                    ? 'bg-n-brand/10 text-n-slate-12 ring-1 ring-n-brand'
                    : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
                "
              >
                <input
                  v-model="monthlyType"
                  type="radio"
                  value="day_of_week"
                  class="!size-4 !w-4 shrink-0 accent-n-brand"
                />
                {{
                  t(
                    'SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.MONTHLY_ON_WEEKDAY'
                  )
                }}
              </label>
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.ENDS') }}
          </h4>

          <div class="overflow-hidden rounded-xl ring-1 ring-n-weak">
            <label
              v-for="(option, index) in END_OPTIONS"
              :key="option.value"
              class="flex min-h-12 cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors"
              :class="[
                endType === option.value
                  ? 'bg-n-alpha-2'
                  : 'bg-n-solid-1 hover:bg-n-alpha-1',
                index ? 'border-t border-n-weak' : '',
              ]"
            >
              <Icon
                :icon="option.icon"
                class="size-4 shrink-0"
                :class="
                  endType === option.value ? 'text-n-brand' : 'text-n-slate-10'
                "
              />

              <span
                class="min-w-0 text-sm font-medium text-n-slate-12"
                :class="
                  endType === option.value && option.value !== 'never'
                    ? 'shrink-0'
                    : 'flex-1'
                "
              >
                {{
                  t(
                    `SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.${option.labelKey}`
                  )
                }}
              </span>

              <input
                v-if="option.value === 'on_date' && endType === 'on_date'"
                v-model="endDate"
                type="date"
                class="!h-8 !w-44 min-w-0 flex-1 rounded-lg bg-n-solid-1 px-2 text-sm text-n-slate-12 outline-none ring-1 ring-n-weak transition-[box-shadow] focus:ring-n-brand"
                @click.stop
              />

              <div
                v-if="
                  option.value === 'after_count' && endType === 'after_count'
                "
                class="flex min-w-0 flex-1 items-center justify-end gap-2"
                @click.stop
              >
                <input
                  v-model.number="endCount"
                  type="number"
                  min="1"
                  class="!h-8 !w-16 shrink-0 rounded-lg bg-n-solid-1 px-2 text-center text-sm font-medium tabular-nums text-n-slate-12 outline-none ring-1 ring-n-weak transition-[box-shadow] focus:ring-n-brand"
                />
                <span class="truncate text-xs text-n-slate-11">
                  {{
                    t(
                      'SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.ENDS_OCCURRENCES'
                    )
                  }}
                </span>
              </div>

              <input
                v-model="endType"
                type="radio"
                :value="option.value"
                class="!size-4 !w-4 shrink-0 accent-n-brand"
              />
            </label>
          </div>
        </section>
      </div>

      <div
        class="mt-6 flex items-center justify-end gap-2 border-t border-n-weak px-6 py-4"
      >
        <NextButton
          ghost
          slate
          sm
          :label="t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.CANCEL')"
          @click="onCancel"
        />
        <NextButton
          solid
          blue
          sm
          :label="t('SCHEDULED_MESSAGES.RECURRENCE.CUSTOM_MODAL.DONE')"
          :disabled="!isValid"
          @click="onDone"
        />
      </div>
    </div>
  </woot-modal>
</template>
